import XUIButton from '@xero/xui/react/button';
import XUIModal, { XUIModalBody, XUIModalFooter, XUIModalHeader } from '@xero/xui/react/modal';
import { XUIActions } from '@xero/xui/react/actions';
import React, { FC, useState, useEffect } from 'react';
import Field from '../../components/Field';
import lang from '../../static/lang/en-NZ/shopify';
import { errorCodes } from '../../static/constants';
import { useSelector } from 'react-redux';
import { clearingAccountSelectors } from '../../redux/slices/AccountSetupSlice';

export interface IClearingAccountModalProps {
    headerText: string;
    messageText: string;
    accountNameLabel: string;
    accountNameHint: string;
    defaultBankAccountName: string;
    cancelButtonText: string;
    createButtonText: string;
    isModalActive: boolean;
    onSecondaryClicked: () => void;
    onPrimaryClicked: (data: IClearingAccount) => Promise<void>;
}

export interface IClearingAccount {
    bankAccountName: string;
    bankAccountId: string;
}

export const ClearingAccountModal: FC<IClearingAccountModalProps> = ({
    headerText,
    messageText,
    accountNameLabel,
    accountNameHint,
    defaultBankAccountName,
    cancelButtonText,
    createButtonText,
    isModalActive,
    onSecondaryClicked,
    onPrimaryClicked,
}) => {
    const isCreating = useSelector(clearingAccountSelectors.isCreating);
    const error = useSelector(clearingAccountSelectors.error) as any;
    const [showClearingAccountError, setShowClearingAccountError] = useState(false);
    const [clearingAccountErrorText, setClearingAccountErrorText] = useState(
        lang.SETUP_PAGE.CLEARING_ACCOUNT.CREATE_ACCOUNT_ERROR
    );
    const [bankAccountName, setBankAccountName] = useState(defaultBankAccountName);

    useEffect(() => {
        setBankAccountName(defaultBankAccountName);
    }, [defaultBankAccountName]);

    useEffect(() => {
        if (isModalActive) {
            // set bank account name to recommended name everytime modal opens
            setBankAccountName(defaultBankAccountName);
        }
    // All dependencies used in a hook should be injected here.
    // This is to ensure that the application reacts to all state and state does not get out of sync.
    // When working in this hook please take the time to refactor, remove this comment and resolve the dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModalActive]);

    useEffect(() => {
        if (error) {
            const handleErrorAsync = async () => {
                let errorText = lang.SETUP_PAGE.CLEARING_ACCOUNT.CREATE_ACCOUNT_ERROR;
                if (error.code && error.code === 400 && error.body) {
                    const body = await error.body;
                    if (body.errorCode && body.errorCode === errorCodes.ACCOUNT_NAME_NOT_UNIQUE) {
                        errorText = lang.SETUP_PAGE.CLEARING_ACCOUNT.ACCOUNT_NAME_NOT_UNIQUE_ERROR_MESSAGE;
                    }
                }
                setClearingAccountErrorText(errorText);
                setShowClearingAccountError(true);
            };

            handleErrorAsync();
        } else {
            setShowClearingAccountError(false);
        }
    }, [error]);

    const onAccountNameChange = (_: string, value: string) => setBankAccountName(value);

    const onSubmit = () => {
        onPrimaryClicked({ bankAccountName, bankAccountId: '' });
    };

    const onClose = () => {
        setShowClearingAccountError(false);
        onSecondaryClicked();
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            onSubmit();
        }
    };
    return (
        <XUIModal isOpen={isModalActive} isForm={true} closeButtonLabel='Close'>
            <XUIModalHeader qaHook='clearing-account-modal--header'>{headerText}</XUIModalHeader>
            <XUIModalBody qaHook='clearing-account-modal--body' className='xui-padding'>
                {
                    <>
                        <div data-automationid='clearing-account-modal-message' className='xui-padding-bottom-large'>
                            {messageText}
                        </div>
                        <Field
                            autoFocus={true}
                            id='clearing-account-modal-input-bank-account-name'
                            label={accountNameLabel}
                            defaultValue={defaultBankAccountName}
                            onChange={onAccountNameChange}
                            hintMessage={accountNameHint}
                            validationMessage={clearingAccountErrorText}
                            isInvalid={showClearingAccountError}
                            maxLength={150}
                            onKeyDown={onKeyDown}
                        />
                    </>
                }
            </XUIModalBody>
            <XUIModalFooter
                qaHook='clearing-account-modal--footer'
                className='xui-actions xui-actions-layout xui-padding-large'
            >
                <XUIActions
                    secondaryAction={
                        <XUIButton
                            qaHook='clearing-account-modal--secondaryAction'
                            className='xui-actions--secondary'
                            onClick={onClose}
                        >
                            {cancelButtonText}
                        </XUIButton>
                    }
                    primaryAction={
                        <XUIButton
                            qaHook='clearing-account-modal--primaryAction'
                            className='xui-actions--primary'
                            onClick={onSubmit}
                            isLoading={isCreating}
                            loadingAriaLabel='Loading'
                            variant='main'
                        >
                            {createButtonText}
                        </XUIButton>
                    }
                />
            </XUIModalFooter>
        </XUIModal>
    );
};
